import React, { PropsWithChildren, RefObject, useEffect } from 'react';

import { windowLogger } from '../../../_lib/Logger/windowLogger';
import { KeyEvent } from '../../../types/KeyEvent';

import { TooltipPlacement } from './Tooltip.enums';
import { computeFlippedState } from './TooltipPositionUtils';

/** Minimal Popover API types for TS compatibility */

interface PopoverHTMLElement extends HTMLElement {
  popover: string;
  showPopover(options?: { source?: Element }): void;
  hidePopover(): void;
}

type TooltipProps = {
  attachTo: RefObject<HTMLElement | HTMLAnchorElement | null>;
  placement?: TooltipPlacement;
  hideTooltipFromScreenReader?: boolean;
  hideOnClick?: boolean;
};

/** Delay before tooltip appears (ms) */
const SHOW_DELAY = 400;

/** Delay before tooltip disappears after trigger leaves (ms) – must allow mouse to reach tooltip */
const HIDE_DELAY = 150;

/** Grace period after click during which tooltip won't reappear (ms) */
const CLICK_GRACE_PERIOD = 500;

let popoverIdCounter = 0;

function generatePopoverId(): string {
  return `tooltip-${++popoverIdCounter}`;
}

/** Cache for shared popovers keyed by tooltip text. Each entry tracks how many triggers reference it. */
interface PopoverCacheEntry {
  element: PopoverHTMLElement;
  refCount: number;
}

const popoverCache = new Map<string, PopoverCacheEntry>();

/** Get or create a shared popover for the given text. Returns [popover, isNew] tuple. */
function getOrCreatePopover(text: string): [PopoverHTMLElement, boolean] {
  const existing = popoverCache.get(text);
  if (existing) {
    existing.refCount++;
    return [existing.element, false];
  }
  const popover = document.createElement('span') as PopoverHTMLElement;
  popover.className = 'tooltip';
  popover.popover = 'hint';
  const id = generatePopoverId();
  popover.id = id;

  const container = document.createElement('span');
  container.className = 'tooltip__container';
  container.textContent = text;
  popover.appendChild(container);

  popover.setAttribute('role', 'tooltip');
  document.body.appendChild(popover);

  popoverCache.set(text, { element: popover, refCount: 1 });
  return [popover, true];
}

/** Release a cached popover. Removes from DOM when last reference is released. */
function releaseCachedPopover(text: string) {
  const entry = popoverCache.get(text);
  if (!entry) return;
  entry.refCount--;
  if (entry.refCount <= 0) {
    entry.element.hidePopover();
    entry.element.remove();
    popoverCache.delete(text);
  }
}

/** Check if CSS anchor positioning (position-area) with flip fallbacks is supported */
const supportsAnchorPositioning =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('position-area', 'top') &&
  CSS.supports('position-try-fallbacks', 'flip-block');

const placementToModifier: Record<TooltipPlacement, string> = {
  [TooltipPlacement.TOP]: 'tooltip--top',
  [TooltipPlacement.BOTTOM]: 'tooltip--bottom',
  [TooltipPlacement.LEFT]: 'tooltip--left',
  [TooltipPlacement.RIGHT]: 'tooltip--right',
  [TooltipPlacement.AUTO]: 'tooltip--top',
};

/**
 * Keeps the arrow on the side pointing to the trigger when the tooltip was flipped
 * (position-try-fallbacks or JS fallback placed it on the opposite side).
 */
function applyFlippedModifier(attachToElement: HTMLElement, popover: PopoverHTMLElement, placement: TooltipPlacement) {
  const flipped = computeFlippedState(
    placement,
    attachToElement.getBoundingClientRect(),
    popover.getBoundingClientRect()
  );
  popover.classList.toggle('tooltip--flipped', flipped);
}

function createNativePopoverTooltip(
  attachToElement: HTMLElement,
  popover: PopoverHTMLElement,
  title: string,
  placement: TooltipPlacement,
  hideTooltipFromScreenReader: boolean,
  hideOnClick: boolean,
  isNewPopover: boolean
) {
  attachToElement.removeAttribute('title');

  const popoverId = popover.id;
  attachToElement.setAttribute('data-has-tooltip', '');

  // tooltipp as aria-describedby, see:

  // https://www.barrierefreies-webdesign.de/artikel/gestaltung/animierte-und-dynamische-inhalte/automatische-popovers/tooltips/popover-api.html
  const originalDescribedBy = attachToElement.getAttribute('aria-describedby');
  if (!hideTooltipFromScreenReader) {
    if (originalDescribedBy) {
      attachToElement.setAttribute('aria-describedby', `${originalDescribedBy} ${popoverId}`);
    } else {
      attachToElement.setAttribute('aria-describedby', popoverId);
    }
  }

  if (isNewPopover) {
    const modifier = placementToModifier[placement] || placementToModifier[TooltipPlacement.AUTO];
    popover.classList.add(modifier);
  }

  if (hideTooltipFromScreenReader) {
    popover.removeAttribute('role');
  }

  let showTimeout: ReturnType<typeof setTimeout> | null = null;
  let hideTimeout: ReturnType<typeof setTimeout> | null = null;
  let clickGraceUntil = 0;

  const show = () => {
    if (Date.now() < clickGraceUntil) {
      return;
    }
    if (showTimeout != null) {
      clearTimeout(showTimeout);
      showTimeout = null;
    }
    if (hideTimeout != null) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    if (!popover.matches(':popover-open')) {
      showTimeout = setTimeout(async () => {
        // The popover may have been released and removed from the DOM while the timer was pending
        // (e.g. the trigger unmounted before the show delay elapsed). showPopover() throws on
        // disconnected elements, so guard against it.
        if (Date.now() >= clickGraceUntil && popover.isConnected && !popover.matches(':popover-open')) {
          popover.showPopover({ source: attachToElement });
          // Load and apply fallback positioning only when needed
          if (!supportsAnchorPositioning) {
            const { positionPopoverFallback } = await import('./TooltipPositionUtils');
            requestAnimationFrame(() => {
              positionPopoverFallback(attachToElement, popover, placement);
            });
          }
          // keep the arrow on the side pointing to the trigger when the tooltip was flipped
          requestAnimationFrame(() => {
            applyFlippedModifier(attachToElement, popover, placement);
          });
        }
      }, SHOW_DELAY);
    }
  };

  // timeout in hide to make sure, you can select text in tooltip
  const hide = () => {
    if (showTimeout != null) {
      clearTimeout(showTimeout);
      showTimeout = null;
    }
    hideTimeout = setTimeout(() => {
      if (popover.matches(':popover-open')) {
        popover.classList.remove('tooltip--flipped');
        popover.hidePopover();
      }
    }, HIDE_DELAY);
  };

  const hideNow = () => {
    if (showTimeout != null) {
      clearTimeout(showTimeout);
    }
    if (hideTimeout != null) {
      clearTimeout(hideTimeout);
    }
    showTimeout = null;
    hideTimeout = null;
    popover.classList.remove('tooltip--flipped');
    popover.hidePopover();
  };

  const onClick = () => {
    clickGraceUntil = Date.now() + CLICK_GRACE_PERIOD;
    hideNow();
  };

  const onKeyDown = (e: Event) => {
    const event = e as KeyboardEvent;
    if (event.code === KeyEvent.Space || event.code === KeyEvent.Enter) {
      hideNow();
    }
  };

  attachToElement.addEventListener('mouseenter', show);
  attachToElement.addEventListener('mouseleave', hide);
  attachToElement.addEventListener('focus', show);
  attachToElement.addEventListener('blur', hide);

  popover.addEventListener('mouseenter', show);
  popover.addEventListener('mouseleave', hide);
  attachToElement.addEventListener('keydown', onKeyDown);
  if (hideOnClick) {
    attachToElement.addEventListener('click', onClick);
  }

  return () => {
    // Restore the title attribute only if the tooltip removed it and React did not
    // replace it with a new title (e.g. a changed title text) in the meantime.
    if (!attachToElement.hasAttribute('title')) {
      attachToElement.setAttribute('title', title);
    }
    attachToElement.removeAttribute('data-has-tooltip');
    if (!hideTooltipFromScreenReader) {
      if (originalDescribedBy != null) {
        attachToElement.setAttribute('aria-describedby', originalDescribedBy);
      } else {
        attachToElement.removeAttribute('aria-describedby');
      }
    }
    attachToElement.removeEventListener('mouseenter', show);
    attachToElement.removeEventListener('mouseleave', hide);
    attachToElement.removeEventListener('focus', show);
    attachToElement.removeEventListener('blur', hide);
    popover.removeEventListener('mouseenter', show);
    popover.removeEventListener('mouseleave', hide);
    attachToElement.removeEventListener('keydown', onKeyDown);
    if (hideOnClick) {
      attachToElement.removeEventListener('click', onClick);
    }
    if (showTimeout != null) {
      clearTimeout(showTimeout);
    }
    if (hideTimeout != null) {
      clearTimeout(hideTimeout);
    }
    popover.classList.remove('tooltip--flipped');
    popover.hidePopover();
    releaseCachedPopover(title);
  };
}

/**
 * Tooltips using the native Popover API (popover="hint").
 *
 * Features:
 * - Hover AND focus triggers
 * - Show delay (400ms) before appearing
 * - Hide delay (configurable via HIDE_DELAY)
 * - Click hides tooltip and prevents immediate reappear (500ms grace period)
 * - Configurable placement via CSS anchor positioning
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/Popover_API
 */
export function Tooltip({
  attachTo,
  hideOnClick = true,
  placement = TooltipPlacement.AUTO,
  hideTooltipFromScreenReader = false,
  children,
}: PropsWithChildren<TooltipProps>) {
  useEffect(() => {
    if (!attachTo.current || !attachTo.current.title) {
      return;
    }

    // Popover API not available (e.g. JSDOM tests) – skip tooltip creation
    if (!Object.hasOwn(HTMLElement.prototype, 'popover')) {
      return;
    }

    try {
      const attachToElement = attachTo.current;
      const title = attachToElement.title;
      const [popover, isNew] = getOrCreatePopover(title);

      return createNativePopoverTooltip(
        attachToElement,
        popover,
        title,
        placement,
        hideTooltipFromScreenReader,
        hideOnClick,
        isNew
      );
    } catch (e) {
      windowLogger.warn(`could not create tooltip for element ${attachTo}`, e);
    }

    return;
  }, [attachTo, attachTo.current?.title, hideOnClick, hideTooltipFromScreenReader, placement]);

  return <>{children}</>;
}
